import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  ForwardRefRenderFunction,
} from 'react';
import { useSelector } from 'react-redux';
import { FormControl, FormHelperText, Grid, Typography } from '@mui/material';

import Input from 'components/Common/InputComponents/Input';
import { InstancesCreationCriteriaError } from '../CreateForm';
import Switch, { SwitchRef } from '../../../../Common/InputComponents/Switch';

import { DataSourceAlarmMetadata, RootState } from 'types/types';

interface BreachingPeriodsChecksProps {
  breachNumber?: number;
  latestIntervalNumber?: number;
  validationErrors: InstancesCreationCriteriaError;
  dataSourceMetadataForAlarm?: DataSourceAlarmMetadata;
  handleOnChange: (event: string, field: string) => void;
}
export interface BreachingPeriodsChecksRef {
  breachingSelected: boolean;
  maxRetentionNumber: number;
  breachNumber: React.RefObject<HTMLInputElement>;
  latestIntervalNumber: React.RefObject<HTMLInputElement>;
  handleMaxRetentionNumber: (retentionGranularity: string) => void;
}

const BreachingPeriodsChecks: ForwardRefRenderFunction<
  BreachingPeriodsChecksRef,
  BreachingPeriodsChecksProps
> = (
  {
    breachNumber,
    handleOnChange,
    validationErrors,
    latestIntervalNumber,
    dataSourceMetadataForAlarm,
  },
  ref
) => {
  const maxRetentionNumbersMetaData = useSelector(
    (state: RootState) =>
      state.applicationState.applicationSettings
        .ALARM_RETENTIONEXECUTIONLOGS_NUMBEROFPERIODS
  );

  const switchRef = useRef<SwitchRef>(null);
  const breachNumberRef = useRef<HTMLInputElement>(null);
  const latestIntervalsNumberRef = useRef<HTMLInputElement>(null);

  const [switchChecked, setSwitchChecked] = useState<boolean>(false);
  const [maxRetentionNumber, setMaxRetentionNumber] = useState<number>(
    maxRetentionNumbersMetaData.DEFAULT
  );

  const handleSwitchCheck = () => {
    setSwitchChecked((prev) => !prev);
  };

  const handleMaxRetentionNumber = (retentionGranularity: string) => {
    if (
      Object.keys(maxRetentionNumbersMetaData).includes(retentionGranularity)
    ) {
      const retentionValue = maxRetentionNumbersMetaData[retentionGranularity];
      setMaxRetentionNumber(retentionValue);
      return retentionValue;
    } else {
      setMaxRetentionNumber(maxRetentionNumbersMetaData.DEFAULT);
    }
  };

  useImperativeHandle(ref, () => ({
    breachingSelected: switchChecked,
    breachNumber: breachNumberRef,
    latestIntervalNumber: latestIntervalsNumberRef,
    maxRetentionNumber,
    handleMaxRetentionNumber,
  }));

  useEffect(() => {
    if (breachNumber || latestIntervalNumber) {
      setSwitchChecked(true);
      switchRef.current?.handleCheckSwitch(true);
    }
  }, [breachNumber, latestIntervalNumber]);

  useEffect(() => {
    handleOnChange(
      latestIntervalsNumberRef.current?.value ?? '',
      'latestInterval'
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxRetentionNumber]);

  return (
    <React.Fragment>
      <Grid item xs={10} style={{ paddingTop: '0' }}>
        <Typography className="form-element-label">
          Breaching Periods Checks
        </Typography>
        <FormControl fullWidth className="exclude-weekends-container">
          <Switch
            handleOnChange={handleSwitchCheck}
            disabled={!dataSourceMetadataForAlarm}
            ref={switchRef}
            className={`exclude-weekends-switch`}
          />
        </FormControl>
      </Grid>
      {switchChecked && (
        <Grid item xs={8} style={{ paddingTop: '0' }}>
          <div className="alarm-breach-inputs">
            <div className="breach-text">Alarm must breach</div>
            <div>
              <FormControl
                sx={{ width: '14rem' }}
                fullWidth
                error={!!validationErrors.breachedIntervals}
              >
                <Input
                  inputProps={{ min: 0 }}
                  type="number"
                  value={breachNumber}
                  inputRef={breachNumberRef}
                  size="small"
                  placeholder="# of breached intervals"
                  handleOnChange={(event) =>
                    handleOnChange(event ?? '', 'breachedIntervals')
                  }
                />
                <FormHelperText>
                  {validationErrors.breachedIntervals}
                </FormHelperText>
              </FormControl>
            </div>
            <div className="breach-text">periods out of the latest</div>
            <div>
              <FormControl
                sx={{ width: '14rem' }}
                fullWidth
                error={!!validationErrors.latestInterval}
              >
                <Input
                  sx={{ width: '14rem' }}
                  inputProps={{ min: 0, max: maxRetentionNumber }}
                  type="number"
                  value={latestIntervalNumber}
                  inputRef={latestIntervalsNumberRef}
                  size="small"
                  placeholder="# of latest intervals"
                  handleOnChange={(event) =>
                    handleOnChange(event ?? '', 'latestInterval')
                  }
                />
                <FormHelperText>
                  {validationErrors.latestInterval}
                </FormHelperText>
              </FormControl>
            </div>
            <div className="breach-text">evaluated periods.</div>
          </div>
        </Grid>
      )}
    </React.Fragment>
  );
};

export default forwardRef<
  BreachingPeriodsChecksRef,
  BreachingPeriodsChecksProps
>(BreachingPeriodsChecks);
